import { useEffect, useRef } from 'react';

interface UseParallaxOptions {
  speed?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  disabled?: boolean;
}

export const useParallax = (options: UseParallaxOptions = {}) => {
  const { speed = 0.5, direction = 'up', disabled = false } = options;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) return;

    const element = ref.current;
    if (!element) return;

    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const rect = element.getBoundingClientRect();
      const elementTop = rect.top + scrolled;
      const windowHeight = window.innerHeight;

      // Only apply parallax when element is in viewport
      if (scrolled + windowHeight > elementTop && scrolled < elementTop + rect.height) {
        const yPos = (scrolled - elementTop) * speed;

        let transform = '';
        switch (direction) {
          case 'up':
            transform = `translateY(${-yPos}px)`;
            break;
          case 'down':
            transform = `translateY(${yPos}px)`;
            break;
          case 'left':
            transform = `translateX(${-yPos}px)`;
            break;
          case 'right':
            transform = `translateX(${yPos}px)`;
            break;
        }

        element.style.transform = transform;
      }
    };

    // Use requestAnimationFrame for smooth performance
    let ticking = false;
    const handleScrollThrottled = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScrollThrottled, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollThrottled);
    };
  }, [speed, direction, disabled]);

  return ref;
};

export default useParallax;